/**
 * LiveTrackingPanel — "Follow Live" entry point, embedded in a race prediction
 * (own or club-shared, both render through RunningAnalysis). Attaching a link
 * navigates to the dedicated live-tracking page — one URL, same content for
 * the owner, club members, or anyone the link is shared with.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { shareLiveTracking, deleteLiveTracking } from '../../../api/runningApi';
import LiveTrackingDialog from './LiveTrackingDialog';

export default function LiveTrackingPanel({ predictionId, clubPredictionId, initialLinkId }) {
  const navigate = useNavigate();
  const [linkId, setLinkId] = useState(initialLinkId || null);
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);
  const [opening, setOpening] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  useEffect(() => {
    setLinkId(initialLinkId || null);
  }, [initialLinkId]);

  const handleAttached = (result) => {
    setLinkId(result?.link_id ?? linkId);
    if (result?.share_url) {
      // Same-tab in-SPA navigation — mobile browsers (notably Safari) block
      // window.open() once it's not perfectly synchronous with the click (we
      // await an API call first), so a new tab silently fails to open there.
      navigate(result.share_url);
    }
  };

  const handleClick = async () => {
    if (!linkId) {
      setAttachDialogOpen(true);
      return;
    }
    // Already following — reopen the same live page rather than asking for the URL again.
    setOpening(true);
    try {
      const result = await shareLiveTracking(linkId);
      handleAttached(result);
    } finally {
      setOpening(false);
    }
  };

  const handleUnlink = async () => {
    if (!linkId) return;
    setUnlinking(true);
    try {
      await deleteLiveTracking(linkId);
      setLinkId(null);
    } catch (err) {
      console.error('Failed to stop following:', err);
    } finally {
      setUnlinking(false);
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Button
        variant="outlined"
        size="small"
        startIcon={opening ? <CircularProgress size={14} /> : <SensorsIcon />}
        onClick={handleClick}
        disabled={opening}
        sx={{ textTransform: 'none' }}
      >
        {linkId ? 'View Live' : 'Follow Live'}
      </Button>
      {linkId && (
        <Tooltip title="Stop following — attach a different runner to this prediction">
          <span>
            <IconButton size="small" onClick={handleUnlink} disabled={unlinking}>
              {unlinking ? <CircularProgress size={14} /> : <LinkOffIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      <LiveTrackingDialog
        open={attachDialogOpen}
        onClose={() => setAttachDialogOpen(false)}
        predictionId={predictionId}
        clubPredictionId={clubPredictionId}
        onAttached={handleAttached}
      />
    </Box>
  );
}
